import type { IApiError, IApiErrorDetail } from '@booking-portal/types/api';
import { getApiError } from '@booking-portal/utils/error-utils';
import { isAxiosError } from 'axios';
import type { Maybe } from 'shared/types';
import { computed, type ComputedRef, type MaybeRef, ref } from 'vue';

export interface IUseApiErrorParserReturnType {
  isApiError: ComputedRef<boolean>;

  /**
   * Matches if the root code matches the given string or regex
   */
  isErrorType(type: string | RegExp): boolean;

  /**
   * Returns true if any of the detail entries contains a code that matches the given string or regex
   */
  someErrorDetailsContains(type: string | RegExp): boolean;

  /**
   * Returns true if any code property inside the object (in the root or in the details) contains the given string or regex
   */
  anyErrorCodeContains(type: string | RegExp): boolean;

  /**
   * Returns the error detail objects
   */
  errorDetails: ComputedRef<IApiErrorDetail[] | undefined>;

  /**
   * Returns the error detail codes as array
   */
  errorDetailCodes: ComputedRef<string[] | undefined>;

  /**
   * Returns the root error code
   */
  errorType: ComputedRef<string | undefined>;

  /**
   * Returns the http status
   */
  status: ComputedRef<number | undefined>;
}

function isErrorType(error: Maybe<IApiError>, type: string | RegExp): boolean {
  const code = error?.error?.code;

  if (type instanceof RegExp) {
    return !!code?.match(type);
  }

  return code === type;
}

function someErrorDetailsContains(
  error: Maybe<IApiError>,
  type: string | RegExp
) {
  const errorDetailCodes = error?.error.details?.map((detail) => detail.code);

  if (type instanceof RegExp) {
    return !!errorDetailCodes?.some((code) => type.test(code));
  }

  return !!errorDetailCodes?.includes(type);
}

function anyErrorCodeContains(error: Maybe<IApiError>, type: string | RegExp) {
  const checkErrorType = isErrorType(error, type);

  if (checkErrorType) {
    return true;
  }

  return someErrorDetailsContains(error, type);
}

export function parseApiError(apiError: IApiError) {
  return {
    isErrorType: (type: string | RegExp) => isErrorType(apiError, type),
    someErrorDetailsContains: (type: string | RegExp) =>
      someErrorDetailsContains(apiError, type),
    anyErrorCodeContains: (type: string | RegExp) =>
      anyErrorCodeContains(apiError, type),
    errorDetails: apiError.error?.details,
    errorDetailCodes: apiError.error.details?.map((detail) => detail.code),
    errorType: apiError.error?.code,
  };
}

export function useApiErrorParser(
  error?: MaybeRef<Maybe<unknown>>
): IUseApiErrorParserReturnType {
  const parsedError = computed(() => {
    const apiError = getApiError(error);
    if (apiError) {
      return parseApiError(apiError);
    }

    return undefined;
  });
  const givenError = ref(error);

  const status = computed(() => {
    if (givenError.value && isAxiosError(givenError.value)) {
      return givenError.value?.status || undefined;
    }

    return undefined;
  });

  return {
    isApiError: computed(() => !!parsedError.value),
    isErrorType: (type: string | RegExp): boolean => {
      return parsedError.value ? parsedError.value.isErrorType(type) : false;
    },
    someErrorDetailsContains: (type: string | RegExp) =>
      parsedError.value
        ? parsedError.value.someErrorDetailsContains(type)
        : false,
    anyErrorCodeContains: (type: string | RegExp) =>
      parsedError.value ? parsedError.value.anyErrorCodeContains(type) : false,
    errorDetails: computed(() => parsedError.value?.errorDetails),
    errorDetailCodes: computed(() => parsedError.value?.errorDetailCodes),
    errorType: computed(() => parsedError.value?.errorType),
    status,
  };
}
